import React from 'react';
import { FileText, Link as LinkIcon, ChevronLeft } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { UploadSource } from './contexts/types';

interface SourceChoiceProps {
  sourceChoice: UploadSource;
  setSourceChoice: (source: UploadSource) => void;
  onBack: () => void;
}

export const SourceChoice: React.FC<SourceChoiceProps> = ({
  sourceChoice,
  setSourceChoice,
  onBack
}) => {
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="gap-2"
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </Button>
      </div>

      <div className="space-y-2">
        <h2 className="text-2xl font-semibold">Where is your paper?</h2>
        <p className="text-muted-foreground">
          Where is your paper hosted?
        </p>
      </div>
      
      <div className="grid md:grid-cols-2 gap-4">
        {/* arXiv Link Option */}
        <Card 
          className={`cursor-pointer transition-all hover:shadow-lg hover:border-primary/50 ${
            sourceChoice === 'arxiv' 
              ? 'border-primary shadow-md ring-2 ring-primary/20' 
              : 'border-border'
          }`}
          onClick={() => setSourceChoice('arxiv')}
        >
          <CardContent className="p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/20">
                <LinkIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold">Already on arXiv</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Link to existing paper
                </p>
              </div>
            </div>
            
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>
                Your paper is already published on arXiv or other preprint servers
              </p>
              <ul className="space-y-1.5 ml-4 list-disc">
                <li>Paste your arXiv URL</li>
                <li>Automatically fetch metadata</li>
                <li>License validation included</li>
                <li>Start receiving feedback immediately</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Local Upload Option */}
        <Card 
          className={`cursor-pointer transition-all hover:shadow-lg hover:border-primary/50 ${
            sourceChoice === 'local' 
              ? 'border-primary shadow-md ring-2 ring-primary/20' 
              : 'border-border'
          }`}
          onClick={() => setSourceChoice('local')}
        >
          <CardContent className="p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-3 rounded-full bg-purple-100 dark:bg-purple-900/20">
                <FileText className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold">On my computer</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Upload PDF file
                </p>
              </div>
            </div>
            
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>
                Upload your paper directly from your computer
              </p>
              <ul className="space-y-1.5 ml-4 list-disc">
                <li>Upload your PDF file</li>
                <li>Enter paper details manually</li>
                <li>Complete control over metadata</li>
                <li>Request peer reviews from the community</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
